import {
  type FieldMetadata,
  getInputProps,
  useInputControl,
} from '@conform-to/react'
import React, { useId } from 'react'
import { Checkbox, type CheckboxProps } from '../other/checkbox.tsx'
import { Input } from '../other/input.tsx'
import { Label } from '../other/label.tsx'
import { ErrorList, type ListOfErrors } from './form-error-list.tsx'

// TODO: Overhaul the rest of form controls to use Radix UI Theme components
// and name them prefixed with word Form to indicate the use for Forms.
// Might be a good idea in general to just prefix all project-made components
// with word Dex to enhance DX for faster component reuse and indication of
// missing reusable components.

export function getInputPropsWithoutKey(
  meta: FieldMetadata<string | number, any, any>,
  options: any,
) {
  const { key, ...inputProps } = getInputProps(meta, options)
  return inputProps
}

export function Field({
  labelProps,
  inputProps,
  errors,
  hint,
  className,
}: {
  labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
  inputProps: React.InputHTMLAttributes<HTMLInputElement> & { key?: React.Key }
  errors?: ListOfErrors
  hint?: string
  className?: string
}) {
  const fallbackId = useId()
  const { key, ...restInputProps } = inputProps
  const id = restInputProps.id ?? fallbackId
  const errorId = errors?.length ? `${id}-error` : undefined
  return (
    <div key={key} className={className}>
      <Label htmlFor={id} {...labelProps} />
      <Input
        id={id}
        aria-invalid={errorId ? true : undefined}
        aria-describedby={errorId}
        {...restInputProps}
      />
      <div className="min-h-[32px] px-4 pb-3 pt-1">
        {errorId ? (
          <ErrorList id={errorId} errors={errors} />
        ) : hint ? (
          <p className="text-[0.8rem] text-muted-foreground">{hint}</p>
        ) : null}
      </div>
    </div>
  )
}

export function CheckboxField({
  labelProps,
  buttonProps,
  errors,
  className,
}: {
  labelProps: JSX.IntrinsicElements['label']
  buttonProps: CheckboxProps & {
    name: string
    form: string
    value?: string
  }
  errors?: ListOfErrors
  className?: string
}) {
  const { key, defaultChecked, ...checkboxProps } = buttonProps
  const fallbackId = useId()
  const checkedValue = buttonProps.value ?? 'on'
  const input = useInputControl({
    key,
    name: buttonProps.name,
    formId: buttonProps.form,
    initialValue: defaultChecked ? checkedValue : undefined,
  })
  const id = buttonProps.id ?? fallbackId
  const errorId = errors?.length ? `${id}-error` : undefined

  return (
    <div className={className}>
      <div className="flex gap-2">
        <Checkbox
          {...checkboxProps}
          id={id}
          aria-invalid={errorId ? true : undefined}
          aria-describedby={errorId}
          checked={input.value === checkedValue}
          onCheckedChange={state => {
            input.change(state.valueOf() ? checkedValue : '')
            buttonProps.onCheckedChange?.(state)
          }}
          onFocus={event => {
            input.focus()
            buttonProps.onFocus?.(event)
          }}
          onBlur={event => {
            input.blur()
            buttonProps.onBlur?.(event)
          }}
          type="button"
        />
        <label
          htmlFor={id}
          {...labelProps}
          className="self-center text-body-xs text-muted-foreground"
        />
      </div>
      <div className="px-4 pb-3 pt-1">
        {errorId ? <ErrorList id={errorId} errors={errors} /> : null}
      </div>
    </div>
  )
}
